import {FC, useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import {GET, POST} from '../../../../services/api'
import Swal from 'sweetalert2'

type TableCombination = {
  tables: Array<{
    id: number
    tableNumber: string
    capacity: number
  }>
  totalCapacity: number
  wastedSeats: number
  score: number
}

type BookingProposal = {
  bookingId: number
  requiredSeats: number
  combinations: TableCombination[]
  bestCombination: TableCombination | null
  canAllocate: boolean
  reason?: string
}

type AllocationProposal = {
  environmentId: number
  date: string
  hourId: number
  totalBookings: number
  allocatedBookings: number
  pendingBookings: number
  proposals: BookingProposal[]
  summary: {
    totalTablesUsed: number
    totalSeatsUsed: number
    availableTables: number
    availableSeats: number
  }
}

type Booking = {
  id: number
  name: string
  people: number
  date: string
  bookingHourId: number
  email?: string
  phone?: string
  isAllocated?: boolean
  allocationId?: number
}

type BookingHour = {
  id: number | string
  hour: string
  bookingDayId: number | string
  bookingConfigId: number | string
  deletedAt: Date | null
}

type Props = {
  show: boolean
  onClose: () => void
  environmentId: number
  onSuccess: () => void
}

export const TableDistributionModal: FC<Props> = ({show, onClose, environmentId, onSuccess}) => {
  const [loading, setLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedHourId, setSelectedHourId] = useState<number | null>(null)
  const [bookingHours, setBookingHours] = useState<BookingHour[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [selectedBookingIds, setSelectedBookingIds] = useState<Set<number>>(new Set())
  const [proposal, setProposal] = useState<AllocationProposal | null>(null)
  const [expandedBookings, setExpandedBookings] = useState<Set<number>>(new Set())
  const [selectedCombinations, setSelectedCombinations] = useState<Map<number, number>>(new Map())

  useEffect(() => {
    if (show) {
      loadBookingHours()
      // Define data de hoje como padrão
      const today = new Date().toISOString().split('T')[0]
      setSelectedDate(today)
    } else {
      resetModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    if (selectedDate && selectedHourId && bookingHours.length > 0) {
      loadBookings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedHourId])

  // Seleciona a próxima hora inteira quando os horários são carregados
  useEffect(() => {
    if (bookingHours.length > 0 && !selectedHourId) {
      const nextHourId = getNextHourId(bookingHours)
      if (nextHourId) {
        setSelectedHourId(nextHourId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingHours])

  const resetModal = () => {
    setProposal(null)
    setBookings([])
    setSelectedBookingIds(new Set())
    setExpandedBookings(new Set())
    setSelectedCombinations(new Map())
  }

  // Encontra a próxima hora inteira a partir da hora atual
  const getNextHourId = (hours: BookingHour[]): number | null => {
    const now = new Date()
    
    // Calcula a próxima hora inteira
    let nextHour = now.getHours()
    if (now.getMinutes() > 0 || now.getSeconds() > 0) {
      nextHour = (nextHour + 1) % 24
    }
    
    // Formata como "HH:00" (ex: "09:00")
    const nextHourFormatted = `${String(nextHour).padStart(2, '0')}:00`
    
    console.log(`⏰ [getNextHourId] Hora atual: ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}, Próxima hora: ${nextHourFormatted}`)
    
    // Procura por um horário que corresponda à próxima hora
    const matchingHour = hours.find(h => h.hour === nextHourFormatted)
    
    if (matchingHour) {
      console.log(`✅ [getNextHourId] Encontrou horário ${nextHourFormatted} com ID ${matchingHour.id}`)
      return Number(matchingHour.id)
    }
    
    // Se não encontrar exatamente, retorna o primeiro
    console.log(`⚠️ [getNextHourId] Horário ${nextHourFormatted} não encontrado, usando primeiro disponível`)
    return hours.length > 0 ? Number(hours[0].id) : null
  }

  const loadBookingHours = async () => {
    try {
      const res = await GET('/queueReservation/bookingHour')
      if (res.success && res.data) {
        // Filtra apenas horários ativos (deletedAt = null)
        const activeHours = res.data.filter((hour: any) => !hour.deletedAt)
        setBookingHours(activeHours)
      }
    } catch (error) {
      console.error('Erro ao carregar horários:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar horários',
        text: 'Verifique se a rota /queueReservation/bookingHour está configurada no backend NIS',
      })
    }
  }

  const loadBookings = async () => {
    if (!selectedHourId) return

    try {
      setLoading(true)
      
      // Encontra o BookingHour selecionado para pegar o bookingConfigId
      const selectedHour = bookingHours.find(h => Number(h.id) === Number(selectedHourId))
      if (!selectedHour) {
        console.error('Horário selecionado não encontrado:', selectedHourId)
        console.log('Horários disponíveis:', bookingHours.map(h => ({ id: h.id, hour: h.hour })))
        return
      }

      // Pega a hora (string) do BookingHour selecionado para filtrar
      // O backend irá usar essa hora para encontrar as reservas correspondentes
      const res = await POST('/queueReservation/booking/filter-for-distribution', {
        date: selectedDate,
        hour: selectedHour.hour, // Filtrar pela hora (ex: '18:00')
        bookingEnvironmentId: environmentId,
      })
      if (res.success && res.data) {
        setBookings(res.data)
        // Todas as reservas retornadas já estão sem alocação
        // Marcar todas por padrão
        const unallocatedIds = new Set<number>(
          res.data.map((b: Booking) => b.id)
        )
        setSelectedBookingIds(unallocatedIds)
      }
    } catch (error) {
      console.error('Erro ao carregar reservas:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleBookingSelection = (bookingId: number) => {
    // Verifica se o booking está alocado
    const booking = bookings.find((b) => b.id === bookingId)
    if (booking?.isAllocated) {
      return // Não permite selecionar bookings já alocados
    }

    const newSelected = new Set(selectedBookingIds)
    if (newSelected.has(bookingId)) {
      newSelected.delete(bookingId)
    } else {
      newSelected.add(bookingId)
    }
    setSelectedBookingIds(newSelected)
  }

  const toggleAllBookings = () => {
    // Filtra apenas bookings não alocados
    const unallocatedBookings = bookings.filter((b) => !b.isAllocated)
    if (selectedBookingIds.size === unallocatedBookings.length) {
      setSelectedBookingIds(new Set())
    } else {
      setSelectedBookingIds(new Set(unallocatedBookings.map((b) => b.id)))
    }
  }

  const handleProposeAllocation = async () => {
    if (!selectedDate || !selectedHourId) {
      Swal.fire('Atenção', 'Selecione data e horário', 'warning')
      return
    }

    if (selectedBookingIds.size === 0) {
      Swal.fire('Atenção', 'Selecione pelo menos uma reserva para alocar', 'warning')
      return
    }

    try {
      setLoading(true)
      
      // Envia apenas os IDs das reservas selecionadas (convertidos para number)
      // O backend buscará as características completas de cada reserva pelos IDs
      const bookingIds = Array.from(selectedBookingIds).map(id => Number(id))
      
      const res = await POST(
        `/queueReservation/table-management/environments/${environmentId}/propose-allocation`,
        {
          date: selectedDate,
          bookingIds, // IDs das reservas já filtradas e selecionadas
        }
      )

      // A função POST já retorna res.data diretamente, não precisa verificar res.success
      if (res && res.proposals) {
        console.log('🎯 [Proposta recebida]', JSON.stringify(res, null, 2))
        console.log('✅ [Estado proposal antes]:', proposal)
        
        setProposal(res)
        console.log('✅ [Estado proposal depois]:', res)
        
        // Inicializa selectedCombinations com a melhor opção (índice 0)
        const initialSelections = new Map<number, number>()
        res.proposals.forEach((p: BookingProposal) => {
          console.log('📋 [Proposta individual]', p)
          // Converte bookingId para number caso venha como string
          const bookingId = typeof p.bookingId === 'string' ? parseInt(p.bookingId) : p.bookingId
          if (p.canAllocate) {
            initialSelections.set(bookingId, 0)
          }
        })
        setSelectedCombinations(initialSelections)
        console.log('🗺️ [selectedCombinations]:', Array.from(initialSelections.entries()))

        Swal.fire({
          icon: 'success',
          title: 'Distribuição Proposta!',
          html: `
            <div class="text-start">
              <p><strong>Total de reservas:</strong> ${res.totalBookings}</p>
              <p><strong>Podem ser alocadas:</strong> ${res.allocatedBookings}</p>
              <p><strong>Aguardando mesas:</strong> ${res.pendingBookings}</p>
              <p><strong>Mesas que serão usadas:</strong> ${res.summary.totalTablesUsed}</p>
              <p><strong>Lugares ocupados:</strong> ${res.summary.totalSeatsUsed}</p>
            </div>
          `,
        })
      }
    } catch (error: any) {
      Swal.fire('Erro', error?.response?.data?.message || 'Erro ao propor distribuição', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmAllocation = async (mode: 'automatic' | 'manual') => {
    if (!proposal) return

    try {
      setLoading(true)

      const allocations = proposal.proposals
        .filter((p) => p.canAllocate)
        .map((p) => {
          const selectedIndex = selectedCombinations.get(p.bookingId) || 0
          const combination = p.combinations[selectedIndex] || p.bestCombination
          return {
            bookingId: p.bookingId,
            tableIds: combination?.tables.map((t) => t.id) || [],
            allocationMethod: mode,
          }
        })
        .filter((a) => a.tableIds.length > 0)

      const res = await POST('/queueReservation/table-management/allocations/confirm-bulk', {
        allocations,
      })

      // Contar total de allocations confirmadas
      const totalAllocations = res.results?.reduce((sum: number, r: any) => sum + (r.allocations?.length || 0), 0) || 0
      const totalFailed = res.failed || 0

      // res.success é o número de reservas bem-sucedidas
      if (res && res.success !== undefined) {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: `${totalAllocations} mesa${totalAllocations !== 1 ? 's' : ''} alocada${totalAllocations !== 1 ? 's' : ''}${
            totalFailed > 0 ? ` (${totalFailed} falharam)` : ''
          }`,
        })
        onSuccess()
        onClose()
      }
    } catch (error: any) {
      Swal.fire('Erro', error?.response?.data?.message || 'Erro ao confirmar alocações', 'error')
    } finally {
      setLoading(false)
    }
  }

  const toggleExpanded = (bookingId: number) => {
    const newExpanded = new Set(expandedBookings)
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId)
    } else {
      newExpanded.add(bookingId)
    }
    setExpandedBookings(newExpanded)
  }

  const selectCombination = (bookingId: number, index: number) => {
    const newSelections = new Map(selectedCombinations)
    newSelections.set(bookingId, index)
    setSelectedCombinations(newSelections)
  }

  const getBookingDetails = (bookingId: number) => {
    return bookings.find((b) => b.id === bookingId)
  }

  return (
    <Modal show={show} onHide={onClose} size='xl' scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='bi bi-gear-fill me-2'></i>
          Distribuição Automática de Mesas
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Filtros */}
        <div className='card card-flush mb-5'>
          <div className='card-body'>
            <div className='row g-3'>
              <div className='col-md-3'>
                <label className='form-label fw-bold'>Data</label>
                <input
                  type='date'
                  className='form-control'
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-bold'>
                  Horário
                  {bookingHours.length > 0 && (
                    <span className='text-muted ms-2 small'>({bookingHours.length} disponível)</span>
                  )}
                </label>
                <select
                  className='form-select'
                  value={selectedHourId || ''}
                  onChange={(e) => setSelectedHourId(Number(e.target.value))}
                  disabled={bookingHours.length === 0}
                >
                  <option value=''>
                    {bookingHours.length === 0 ? 'Nenhum horário ativo' : 'Selecione...'}
                  </option>
                  {bookingHours.map((hour) => (
                    <option key={hour.id} value={hour.id}>
                      {hour.hour}
                    </option>
                  ))}
                </select>
                {bookingHours.length === 0 && (
                  <div className='form-text text-danger'>
                    <i className='bi bi-exclamation-triangle me-1'></i>
                    Configure horários na tabela bookingHour (campo deletedAt = NULL)
                  </div>
                )}
              </div>
              <div className='col-md-5'>
                <label className='form-label fw-bold d-block'>&nbsp;</label>
                <button
                  type='button'
                  className='btn btn-primary w-100'
                  onClick={handleProposeAllocation}
                  disabled={loading || !selectedDate || !selectedHourId}
                >
                  {loading ? (
                    <span className='spinner-border spinner-border-sm me-2'></span>
                  ) : (
                    <i className='bi bi-magic me-2'></i>
                  )}
                  Propor Distribuição
                </button>
              </div>
            </div>

            {bookings.length > 0 && !proposal && (
              <>
                <div className='alert alert-info mt-3 mb-3 d-flex align-items-center justify-content-between'>
                  <div className='text-muted'>
                    Encontradas <strong>{bookings.length}</strong> reserva(s) para este horário
                    {bookings.filter((b) => b.isAllocated).length > 0 && (
                      <span className='ms-2'>
                        ({bookings.filter((b) => b.isAllocated).length} já alocada
                        {bookings.filter((b) => b.isAllocated).length !== 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    onClick={toggleAllBookings}
                    disabled={bookings.filter((b) => !b.isAllocated).length === 0}
                  >
                    {selectedBookingIds.size === bookings.filter((b) => !b.isAllocated).length
                      ? 'Desmarcar Todas'
                      : 'Marcar Todas'}
                  </button>
                </div>

                <div className='card bg-light-primary border-primary border-dashed'>
                  <div className='card-body p-3'>
                    <div className='fw-bold mb-3 text-primary'>
                      <i className='bi bi-check2-square me-2'></i>
                      Selecione as reservas para alocar ({selectedBookingIds.size} de{' '}
                      {bookings.filter((b) => !b.isAllocated).length} disponíveis)
                    </div>
                    <div className='d-flex flex-column gap-2'>
                      {bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className='form-check form-check-custom form-check-solid bg-white p-3 rounded'
                          style={{opacity: booking.isAllocated ? 0.6 : 1}}
                        >
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id={`booking-${booking.id}`}
                            checked={selectedBookingIds.has(booking.id)}
                            onChange={() => toggleBookingSelection(booking.id)}
                            disabled={booking.isAllocated}
                          />
                          <label
                            className={`form-check-label d-flex align-items-center w-100 ${
                              booking.isAllocated ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            htmlFor={`booking-${booking.id}`}
                          >
                            <div className='flex-grow-1'>
                              <div className='d-flex align-items-center gap-2 mb-1'>
                                <span className='badge badge-light-primary'>#{booking.id}</span>
                                <span className='fw-bold'>{booking.name}</span>
                                {booking.isAllocated && (
                                  <span className='badge badge-success'>
                                    <i className='bi bi-check-circle-fill me-1'></i>
                                    Já Alocada
                                  </span>
                                )}
                              </div>
                              <div className='d-flex flex-column gap-1'>
                                <div className='text-muted small'>
                                  <i className='bi bi-people me-1'></i>
                                  {booking.people} pessoa{booking.people !== 1 ? 's' : ''}
                                </div>
                                {booking.email && (
                                  <div className='text-muted small'>
                                    <i className='bi bi-envelope me-1'></i>
                                    {booking.email}
                                  </div>
                                )}
                                {booking.phone && (
                                  <div className='text-muted small'>
                                    <i className='bi bi-telephone me-1'></i>
                                    {booking.phone}
                                  </div>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Proposta de Distribuição */}
        {proposal && (
          <>
            {console.log('🎨 [Renderizando propostas] proposal:', proposal)}
            {/* Resumo */}
            <div className='card card-flush bg-light-primary mb-5'>
              <div className='card-body p-5'>
                <div className='row text-center'>
                  <div className='col-md-3'>
                    <div className='fs-2 fw-bold text-primary'>{proposal.totalBookings}</div>
                    <div className='text-muted small'>Total de Reservas</div>
                  </div>
                  <div className='col-md-3'>
                    <div className='fs-2 fw-bold text-success'>{proposal.allocatedBookings}</div>
                    <div className='text-muted small'>Podem Ser Alocadas</div>
                  </div>
                  <div className='col-md-3'>
                    <div className='fs-2 fw-bold text-danger'>{proposal.pendingBookings}</div>
                    <div className='text-muted small'>Aguardando Mesas</div>
                  </div>
                  <div className='col-md-3'>
                    <div className='fs-2 fw-bold text-dark'>
                      {proposal.summary.totalTablesUsed}
                    </div>
                    <div className='text-muted small'>Mesas Necessárias</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Propostas */}
            <div className='card card-flush'>
              <div className='card-header'>
                <h3 className='card-title'>Propostas de Alocação</h3>
              </div>
              <div className='card-body p-0'>
                {proposal.proposals.map((bookingProposal, index) => {
                  const booking = getBookingDetails(bookingProposal.bookingId)
                  const isExpanded = expandedBookings.has(bookingProposal.bookingId)
                  const selectedIndex = selectedCombinations.get(bookingProposal.bookingId) || 0
                  const selectedCombo =
                    bookingProposal.combinations[selectedIndex] || bookingProposal.bestCombination

                  return (
                    <div
                      key={bookingProposal.bookingId}
                      className={`border-bottom ${index === 0 ? '' : 'border-top'}`}
                    >
                      <div className='p-5'>
                        {/* Header da Reserva */}
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <div className='d-flex align-items-center'>
                            {bookingProposal.canAllocate ? (
                              <i className='bi bi-check-circle-fill fs-2 text-success me-3'></i>
                            ) : (
                              <i className='bi bi-x-circle-fill fs-2 text-danger me-3'></i>
                            )}
                            <div>
                              <h5 className='mb-1'>
                                Reserva #{bookingProposal.bookingId}
                                {booking && <span className='text-muted'> - {booking.name}</span>}
                              </h5>
                              <div className='text-muted small'>
                                <i className='bi bi-people-fill me-1'></i>
                                {bookingProposal.requiredSeats} pessoa(s)
                              </div>
                            </div>
                          </div>

                          {bookingProposal.canAllocate && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={() => toggleExpanded(bookingProposal.bookingId)}
                            >
                              {isExpanded ? (
                                <>
                                  <i className='bi bi-chevron-up me-1'></i>
                                  Ocultar Opções
                                </>
                              ) : (
                                <>
                                  <i className='bi bi-chevron-down me-1'></i>
                                  Ver Opções ({bookingProposal.combinations.length})
                                </>
                              )}
                            </button>
                          )}
                        </div>

                        {/* Melhor Combinação */}
                        {bookingProposal.canAllocate && selectedCombo && (
                          <div className='alert alert-light-primary border border-primary mb-3'>
                            <div className='d-flex align-items-center justify-content-between'>
                              <div>
                                <strong>Sugestão:</strong>{' '}
                                {selectedCombo.tables.length === 1 ? (
                                  <>Mesa {selectedCombo.tables[0].tableNumber} ({selectedCombo.tables[0].capacity} lugares)</>
                                ) : (
                                  <>
                                    Mesas{' '}
                                    {selectedCombo.tables.map((t) => `${t.tableNumber} (${t.capacity} lugares)`).join(' + ')}
                                  </>
                                )}
                              </div>
                              <div className='text-end'>
                                <div className='small text-muted'>Capacidade Total</div>
                                <div className='fw-bold'>
                                  {selectedCombo.totalCapacity} lugar(es)
                                  {selectedCombo.wastedSeats > 0 && (
                                    <span className='text-warning ms-2'>
                                      ({selectedCombo.wastedSeats} sobrando)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Não pode alocar */}
                        {!bookingProposal.canAllocate && (
                          <div className='alert alert-danger'>
                            <i className='bi bi-exclamation-triangle me-2'></i>
                            {bookingProposal.reason}
                          </div>
                        )}

                        {/* Opções Expandidas */}
                        {isExpanded && bookingProposal.canAllocate && (
                          <div className='mt-3'>
                            <h6 className='text-muted mb-2'>Outras Opções:</h6>
                            <p className='text-muted small mb-3'>
                              <i className='bi bi-info-circle me-1'></i>
                              Clique em uma opção para usar no <strong>Confirmar Manual</strong>. 
                              A <strong>Opção 1</strong> será usada no <strong>Confirmar Automático</strong>.
                            </p>
                            <div className='row g-3'>
                              {bookingProposal.combinations.map((combo, comboIndex) => (
                                <div key={comboIndex} className='col-md-6'>
                                  <div
                                    className={`card cursor-pointer ${
                                      selectedIndex === comboIndex
                                        ? 'border-primary bg-light-primary'
                                        : 'border-light'
                                    }`}
                                    onClick={() =>
                                      selectCombination(bookingProposal.bookingId, comboIndex)
                                    }
                                  >
                                    <div className='card-body p-4'>
                                      <div className='d-flex align-items-center justify-content-between mb-2'>
                                        <div className='fw-bold'>
                                          Opção {comboIndex + 1}
                                          {comboIndex === 0 && (
                                            <span className='badge badge-light-success ms-2'>
                                              Melhor
                                            </span>
                                          )}
                                        </div>
                                        <div className='form-check form-check-custom form-check-solid'>
                                          <input
                                            className='form-check-input'
                                            type='radio'
                                            checked={selectedIndex === comboIndex}
                                            onChange={() => {}}
                                          />
                                        </div>
                                      </div>
                                      <div className='text-muted small mb-2'>
                                        {combo.tables.length === 1 ? (
                                          <>Mesa {combo.tables[0].tableNumber} ({combo.tables[0].capacity} lugares)</>
                                        ) : (
                                          <>Mesas {combo.tables.map((t) => `${t.tableNumber} (${t.capacity} lugares)`).join(' + ')}</>
                                        )}
                                      </div>
                                      <div className='d-flex justify-content-between'>
                                        <span className='small'>
                                          Capacidade: {combo.totalCapacity}
                                        </span>
                                        {combo.wastedSeats > 0 ? (
                                          <span className='small text-warning'>
                                            {combo.wastedSeats} sobrando
                                          </span>
                                        ) : (
                                          <span className='small text-success'>
                                            <i className='bi bi-check-circle me-1'></i>
                                            Sem desperdício
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-between align-items-center'>
        <div className='text-muted small' style={{maxWidth: '50%'}}>
          <i className='bi bi-lightbulb me-1'></i>
          <strong>Automático:</strong> usa Opção 1 (melhor score) | 
          <strong className='ms-2'>Manual:</strong> usa opção selecionada
        </div>
        <div>
          <button type='button' className='btn btn-light me-2' onClick={onClose}>
            Cancelar
          </button>
          {proposal && proposal.allocatedBookings > 0 && (
            <>
            <button
              type='button'
              className='btn btn-warning'
              onClick={() => handleConfirmAllocation('manual')}
              disabled={loading}
              title='Confirma usando as combinações que você selecionou nas opções'
              data-bs-toggle='tooltip'
            >
              <i className='bi bi-hand-index me-2'></i>
              Confirmar Manual
            </button>
            <button
              type='button'
              className='btn btn-success ms-3'
              onClick={() => handleConfirmAllocation('automatic')}
              disabled={loading}
              title='Confirma automaticamente usando a melhor opção (Opção 1) para todas as reservas'
              data-bs-toggle='tooltip'
            >
              <i className='bi bi-magic me-2'></i>
              Confirmação Automática
            </button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}
